import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useTheme } from '../theme/ThemeContext';
import { MENU_STYLE } from '../theme/MenuStyleContext';
import KernelNotificationBell from './KernelNotificationBell';
import './kernelMenu.css';

const themeIcons = {
  light: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="18" x2="12" y2="21" />
    </svg>
  ),
};

const defaultResolveActive = (item, location) => (
  item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
);

const defaultCreateItemClickHandler = ({ closeMenu }) => () => closeMenu;

/**
 * Fabbrica del componente Layout (sidebar principale) condiviso da tutti i
 * moduli che seguono questo schema: 3 stili scelti a build-time tramite
 * MENU_STYLE ("sidebar" con accordion, "hamburger" sempre attivo, "compact"
 * a icone piatte — vedi andrea/README.md).
 *
 * Ogni modulo resta responsabile solo di ciò che lo differenzia:
 * - `icons`: dizionario icone per le voci di menu (deve avere una `home` di fallback).
 * - `brand`: { mode: 'svg', mark: <svg/> } oppure { mode: 'image', src: logoImport }.
 * - `getVisibleSections`: dal proprio `menu.js`.
 * - `resolveActive(item, location)` *(opzionale)*: sostituisce il confronto
 *   generico su `item.path`/`item.end` quando un modulo ha una propria logica
 *   di voce attiva (es. frimmacademy, sensibile alla query string).
 * - `createItemClickHandler({ closeMenu, location })` *(opzionale)*: se un
 *   modulo ha bisogno di un click custom sulle voci oltre a chiudere il menu
 *   mobile (es. ticket, che forza il reload cliccando la voce già attiva).
 * - `showThemePicker` *(opzionale, default true)*: false nasconde il
 *   selettore chiaro/scuro/sistema dal footer della sidebar, per i moduli che
 *   non vogliono offrire il cambio tema.
 * - `logoutRedirectTo` *(opzionale, default '/login')*: pagina su cui
 *   atterrare dopo il logout — per i moduli dove '/' richiede comunque il
 *   login non ha senso cambiarla, ma un modulo come svuotafrigo (usabile
 *   anche da anonimo) vuole tornare alla home pubblica invece che al form di
 *   accesso.
 */
export function createLayout({
  icons,
  brand,
  getVisibleSections,
  resolveActive = defaultResolveActive,
  createItemClickHandler = defaultCreateItemClickHandler,
  showThemePicker = true,
  logoutRedirectTo = '/login',
}) {
  // Ogni pagina rimonta il proprio <Layout>: senza questa memoria la sezione
  // aperta si azzererebbe a ogni navigazione, richiudendo il menu nelle pagine
  // che non corrispondono a nessuna voce (es. il dettaglio /properties/:id).
  let lastOpenSection = null;

  function Layout({ user, onLogout, children, contentClassName }) {
    const { t } = useLanguage();
    const { theme, setTheme } = useTheme();
    const menuStyle = MENU_STYLE;
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [openSection, setOpenSectionState] = useState(lastOpenSection);
    const setOpenSection = (next) => {
      lastOpenSection = typeof next === 'function' ? next(lastOpenSection) : next;
      setOpenSectionState(lastOpenSection);
    };
    const [themeMenuOpen, setThemeMenuOpen] = useState(false);
    const [notice, setNotice] = useState('');
    const themeMenuRef = useRef(null);

    const closeMenu = () => setMenuOpen(false);
    const compact = menuStyle === 'compact';
    const isSidebarStyle = menuStyle === 'sidebar';
    const isItemActive = (item) => resolveActive(item, location);
    const onItemClick = createItemClickHandler({ closeMenu, location });

    const handleLogout = () => {
      onLogout();
      navigate(logoutRedirectTo);
    };

    const initials = user
      ? `${(user.name || '')[0] || ''}${(user.surname || '')[0] || ''}`.toUpperCase()
      : '?';

    const sections = getVisibleSections(user);

    const activeSectionKey = sections.find((section) =>
      section.items.some((item) => isItemActive(item))
    )?.key ?? null;

    // Stile sidebar: la sezione della pagina corrente si apre da sola (accordion,
    // una sola sezione aperta alla volta) — il click manuale su un'altra intestazione
    // può comunque aprirne una diversa prima di navigare.
    useEffect(() => {
      // Nessuna sezione attiva (pagina fuori menu): si lascia com'è, non si chiude.
      if (isSidebarStyle && activeSectionKey) {
        setOpenSection(activeSectionKey);
      } else if (isSidebarStyle && !lastOpenSection) {
        setOpenSection(sections[0]?.key ?? null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSidebarStyle, location.pathname, location.search]);

    // Selettore tema: un solo bottone (icona del tema corrente) che apre un piccolo
    // menu con le 3 scelte, invece della fila di 3 bottoni sempre visibile — lascia
    // più spazio in verticale alla lista delle voci, specie in stile "compact" dove
    // la sidebar è stretta. Si chiude cliccando fuori.
    useEffect(() => {
      if (!themeMenuOpen) return undefined;
      const onClickOutside = (e) => {
        if (themeMenuRef.current && !themeMenuRef.current.contains(e.target)) {
          setThemeMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }, [themeMenuOpen]);

    // Messaggio "contatta l'amministratore" mostrato cliccando una voce non abilitata.
    useEffect(() => {
      if (!notice) return undefined;
      const timer = setTimeout(() => setNotice(''), 5000);
      return () => clearTimeout(timer);
    }, [notice]);

    // Nessun campo "icona" a livello di sezione in menu.js: per l'intestazione dei
    // gruppi nello stile sidebar si usa l'icona della prima voce della sezione.
    const sectionIcon = (section) => {
      const firstIconKey = section.items.find((item) => item.icon)?.icon;
      return icons[firstIconKey] || icons.home;
    };

    const toggleOpenSection = (key) => {
      setOpenSection((prev) => (prev === key ? null : key));
    };

    const renderItem = (item, iconOnly = false) => (
      item.disabled ? (
        <span
          key={item.key}
          className={'nav-item nav-item-disabled' + (iconOnly ? ' nav-item-icon-only' : '')}
          title={t('nav.permissionMissing')}
          role="button"
          tabIndex={0}
          aria-disabled="true"
          onClick={() => setNotice(t('nav.permissionMissingNotice', { name: t(item.labelKey) }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setNotice(t('nav.permissionMissingNotice', { name: t(item.labelKey) }));
          }}
        >
          <span className="nav-icon">{icons[item.icon] || icons.home}</span>{!iconOnly && <> {t(item.labelKey)}</>}
        </span>
      ) : (
        <NavLink
          key={item.key}
          to={item.path}
          end={item.end}
          className={() => 'nav-item' + (isItemActive(item) ? ' active' : '') + (iconOnly ? ' nav-item-icon-only' : '')}
          title={iconOnly ? t(item.labelKey) : undefined}
          onClick={onItemClick(item)}
        >
          <span className="nav-icon">{icons[item.icon] || icons.home}</span>{!iconOnly && <> {t(item.labelKey)}</>}
        </NavLink>
      )
    );

    const brandAlt = `${t('nav.brand1')} ${t('nav.brand2')}`;

    return (
      <div className={`app-shell app-shell--${menuStyle}`}>
        <header className="mobile-topbar">
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-label={t('nav.openMenu')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {brand.mode === 'image' ? (
            <span className="brand-logo brand-logo-mobile">
              <img src={brand.src} alt={brandAlt} />
            </span>
          ) : (
            <span className="mobile-topbar-brand">{t('nav.brand1')} {t('nav.brand2')}</span>
          )}
        </header>
        {menuOpen && <div className="sidebar-backdrop" onClick={closeMenu} />}
        <aside className={'sidebar' + (menuOpen ? ' open' : '')}>
          <div className="sidebar-brand">
            {brand.mode === 'image' ? (
              <span className="brand-logo">
                <img src={brand.src} alt={brandAlt} />
              </span>
            ) : (
              <>
                <span className="brand-mark">{brand.mark}</span>
                <span className="brand-name">{t('nav.brand1')}<br />{t('nav.brand2')}</span>
              </>
            )}
            <button type="button" className="sidebar-close" onClick={closeMenu} aria-label={t('nav.closeMenu')}>
              ×
            </button>
          </div>
          <nav className="sidebar-nav">
            {compact ? (
              sections.flatMap((section) => section.items).map((item) => renderItem(item, true))
            ) : isSidebarStyle ? (
              sections.map((section) => {
                if (section.items.length <= 1) {
                  return <React.Fragment key={section.key}>{section.items.map((item) => renderItem(item))}</React.Fragment>;
                }
                const isOpen = openSection === section.key;
                return (
                  <div className={'nav-group' + (isOpen ? ' open' : '')} key={section.key}>
                    <button type="button" className="nav-group-header" onClick={() => toggleOpenSection(section.key)}>
                      <span className="nav-icon">{sectionIcon(section)}</span>
                      <span className="nav-group-label">{t(section.labelKey)}</span>
                      <span className="nav-group-chevron">›</span>
                    </button>
                    {isOpen && (
                      <div className="nav-group-items">
                        {section.items.map((item) => renderItem(item))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Un'unica sezione = "un solo menu": niente etichetta di gruppo,
              // solo le voci in fila (la label avrebbe senso solo per separare
              // più gruppi tra loro).
              sections.map((section) => (
                <div className="nav-section" key={section.key}>
                  {sections.length > 1 && <div className="nav-section-label">{t(section.labelKey)}</div>}
                  {section.items.map((item) => renderItem(item))}
                </div>
              ))
            )}
          </nav>
          <div className="sidebar-footer">
            {showThemePicker && (
              /* "theme-picker", non "theme-toggle": contabilita usa già quella classe per
                 il selettore standalone (3 bottoni) nella pagina di login. */
              <div className="theme-picker" ref={themeMenuRef}>
                <button
                  type="button"
                  className="theme-toggle-current"
                  onClick={() => setThemeMenuOpen((prev) => !prev)}
                  aria-haspopup="true"
                  aria-expanded={themeMenuOpen}
                  title={t('nav.themeToggleLabel')}
                >
                  <span className="nav-icon">{themeIcons[theme]}</span>
                  <span className="theme-toggle-label">{t(`nav.theme_${theme}`)}</span>
                </button>
                {themeMenuOpen && (
                  <div className="theme-menu" role="menu" aria-label={t('nav.themeToggleLabel')}>
                    {['light', 'dark', 'system'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        role="menuitemradio"
                        aria-checked={theme === option}
                        className={'theme-menu-item' + (theme === option ? ' active' : '')}
                        onClick={() => { setTheme(option); setThemeMenuOpen(false); }}
                      >
                        <span className="nav-icon">{themeIcons[option]}</span>
                        {t(`nav.theme_${option}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="sidebar-user">
              <span className="avatar">{initials}</span>
              <div className="user-info">
                <span className="user-name">{user ? `${user.name} ${user.surname || ''}`.trim() : ''}</span>
                <span className="user-email">{user?.email}</span>
              </div>
            </div>
            <button className="btn btn-outline btn-block" onClick={handleLogout}>
              {t('nav.logout')}
            </button>
          </div>
        </aside>
        {notice && (
          <div className="menu-notice" role="status" onClick={() => setNotice('')}>
            {notice}
          </div>
        )}
        <main className={contentClassName ? `main-content ${contentClassName}` : 'main-content'}>{children}</main>
        {user && <KernelNotificationBell />}
      </div>
    );
  }

  return Layout;
}
